import { supabase } from "./client";
import ErrorHandler from "../utils/ErrorHandler";

/**
 * Log a user activity event
 * Server resolves organizationID (org_id()) and actorPersonID (person_id()) from the JWT,
 * so client only provides:
 * - What was acted upon (subject)
 * - What action was taken (verb)
 * Summaries are rendered at display time from structured fields, not stored.
 * 
 * @param {Object} params
 * @param {string} params.subjectType - 'Person'|'Event'|'EventInstance'|'Task'|'SmallGroup'|'Path'|'Family'|'Tag'|'Note'|etc
 * @param {string} params.subjectID - UUID of the entity acted upon
 * @param {string} params.subjectName - Display name snapshot
 * @param {string} params.verb - 'create'|'update'|'delete'|'assign'|'unassign'|'publish'|'close'|'reopen'|'archive'|'unarchive'
 * @param {string} params.details - Optional detailed description (e.g. changed fields)
 * @returns {Promise<string|null>} - The activity ID or null on error
 */
export const insertActivity = async ({
    subjectType,
    subjectID,
    subjectName,
    verb,
    details,
}) => {
    const { data, error } = await supabase.schema('activity').rpc("log", {
        p_subjectType: subjectType,
        p_subjectID: subjectID,
        p_subjectName: subjectName ?? '',
        p_verb: verb,
        p_details: details,
    });

    if (error) {
        ErrorHandler(
            new Error(error.message),
            { query: "insertActivity", subjectType, verb, error },
            "Failed to log activity"
        );
        return null;
    }

    return data;
};

/**
 * Get paginated activity feed with optional filters
 * Uses offset/limit pagination
 * 
 * @param {Object} params - Filter parameters
 * @param {number} params.limit - Page size (default 20)
 * @param {number} params.offset - Offset for pagination
 * @param {string} params.actorID - Filter by person who performed action
 * @param {string} params.subjectType - Filter by entity type
 * @param {string} params.subjectID - Filter by specific entity
 * @param {string} params.verb - Filter by verb
 * @param {string} params.dateFrom - ISO date string
 * @param {string} params.dateTo - ISO date string
 * @param {string} params.search - ILIKE search on summary text
 * @returns {Promise<{activities: Array, hasMore: boolean}>}
 */
export const getActivityFeed = async (params = {}) => {
    const {
        limit = 20,
        offset = 0,
        actorID = null,
        subjectType = null,
        subjectID = null,
        verb = null,
        dateFrom = null,
        dateTo = null,
        search = null,
    } = params;

    const { data, error } = await supabase.schema('activity').rpc("list", {
        p_limit: limit,
        p_offset: offset,
        p_actorID: actorID,
        p_subjectType: subjectType,
        p_subjectID: subjectID,
        p_verb: verb,
        p_dateFrom: dateFrom,
        p_dateTo: dateTo,
        p_search: search,
    });

    if (error) {
        ErrorHandler(
            new Error(error.message),
            { query: "getActivityFeed", params, error },
            "Failed to load activity feed"
        );
        return { activities: [], hasMore: false };
    }

    const activities = data || [];
    
    // hasMore is on every row, extract from first row
    const hasMore = activities.length > 0 ? activities[0].hasMore : false;

    // Remove hasMore from activity objects before returning
    const cleanActivities = activities.map(({ hasMore: _, ...activity }) => activity);

    return { activities: cleanActivities, hasMore };
};
